/**
 * Workforce Management — client types + time helpers shared by the admin
 * planner and the agent self-service pages. Every instant is ISO-UTC on the
 * wire and rendered in the user's selected timezone.
 */
import { addDays, fillShiftBlocks, wallTimeToInstant, weekdayOf } from '@kc3/types'

export { addDays, wallTimeToInstant, weekdayOf }

export interface ActivityType {
  id: string
  name: string
  code: string
  color: string
  category: string
  isPaid: boolean
  isProductive: boolean
  adherenceStates: string[]
  isSystem: boolean
  sortOrder: number
}

export interface ShiftActivity {
  id?: string
  activityTypeId: string
  startAt: string
  endAt: string
  notes?: string | null
}

export interface Shift {
  id: string
  agentId: string
  agentName: string
  templateId: string | null
  startAt: string
  endAt: string
  status: 'DRAFT' | 'PUBLISHED' | 'CANCELLED'
  source: string
  notes: string | null
  publishedAt: string | null
  activities: ShiftActivity[]
}

export interface TimeOffSpan {
  id: string
  agentId?: string
  type: string
  startAt: string
  endAt: string
  allDay: boolean
  status: string
}

export interface WfmAgent {
  id: string
  name: string
  extension: string | null
  state: string
}

export interface ShiftTemplate {
  id: string
  name: string
  description: string | null
  color: string
  startTime: string
  durationMin: number
  activities: { activityTypeId: string; offsetMin: number; durationMin: number }[]
  daysOfWeek: number[]
  isActive: boolean
  shiftCount?: number
}

export interface WfmSettings {
  intervalMinutes: 15 | 30 | 60
  serviceLevelPct: number
  serviceLevelSec: number
  shrinkagePct: number
  maxOccupancyPct: number
  adherenceGraceMin: number
  adherenceTargetPct: number
  weekStartsOn: number
  swapRequiresApproval: boolean
  timeOffMinNoticeDays: number
}

export interface Block {
  start: string
  end: string
  activity: string
  category?: string
  color: string | null
}

export const TIME_OFF_TYPES = ['VACATION', 'SICK', 'PERSONAL', 'TRAINING', 'OTHER'] as const

export const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const

export function todayIn(tz: string): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: tz })
}

/** First day of the week containing `date`. */
export function weekStart(date: string, weekStartsOn = 1): string {
  const back = (weekdayOf(date) - weekStartsOn + 7) % 7
  return addDays(date, -back)
}

export function rangeDays(from: string, count: number): string[] {
  return Array.from({ length: count }, (_, i) => addDays(from, i))
}

export function localDate(iso: string, tz: string): string {
  return new Date(iso).toLocaleDateString('en-CA', { timeZone: tz })
}

/** 1:26 PM — every WFM time is shown 12-hour with AM/PM. */
export function fmtTime(iso: string, tz: string): string {
  return new Date(iso).toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true })
}

/** "13:25" (a stored HH:MM, e.g. a template start) → "1:25 PM". */
export function fmtClock(hhmm: string): string {
  const [h = 0, m = 0] = hhmm.split(':').map(Number)
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h % 24 < 12 ? 'AM' : 'PM'}`
}

export function fmtDay(date: string, opts: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' }): string {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US', { ...opts, timeZone: 'UTC' })
}

export function fmtDateTime(iso: string, tz: string): string {
  return new Date(iso).toLocaleString('en-US', { timeZone: tz, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
}

export function fmtRange(startIso: string, endIso: string, tz: string): string {
  return `${fmtTime(startIso, tz)} – ${fmtTime(endIso, tz)}`
}

export function hoursBetween(startIso: string, endIso: string): number {
  return Math.round(((Date.parse(endIso) - Date.parse(startIso)) / 3_600_000) * 10) / 10
}

/** `HH:MM` of an instant in tz — for <input type="time">. */
export function timeInput(iso: string, tz: string): string {
  return fmtTime(iso, tz)
}

/** Local date + HH:MM in tz → ISO instant. */
export function toIso(date: string, time: string, tz: string): string {
  return new Date(wallTimeToInstant(date, time, tz)).toISOString()
}

/** End time on/after start: an end clock-time ≤ start rolls to the next day. */
export function toIsoAfter(date: string, time: string, tz: string, afterIso: string): string {
  let iso = toIso(date, time, tz)
  if (Date.parse(iso) <= Date.parse(afterIso)) iso = toIso(addDays(date, 1), time, tz)
  return iso
}

export function fmtDuration(min: number): string {
  const h = Math.floor(min / 60)
  const m = Math.round(min % 60)
  return h > 0 ? `${h}h${m ? ` ${m}m` : ''}` : `${m}m`
}

export function statusTone(status: string): string {
  switch (status) {
    case 'APPROVED':
    case 'PUBLISHED':
      return 'bg-emerald-50 text-emerald-700 ring-emerald-200'
    case 'PENDING':
    case 'PENDING_PEER':
    case 'PENDING_APPROVAL':
    case 'DRAFT':
      return 'bg-amber-50 text-amber-700 ring-amber-200'
    case 'DENIED':
    case 'DECLINED':
      return 'bg-red-50 text-red-700 ring-red-200'
    default:
      return 'bg-slate-100 text-slate-600 ring-slate-200'
  }
}

export function prettyStatus(s: string): string {
  return s.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase())
}

/** Error text from an api client rejection. */
export function errMessage(err: unknown, fallback: string): string {
  const e = err as { body?: { message?: string; error?: string }; message?: string }
  return e?.body?.message ?? e?.body?.error ?? (e?.message && e.message !== 'Request failed' ? e.message : fallback)
}

export function timeToMinutes(t: string): number {
  const [h, m] = t.split(':').map(Number)
  return (h ?? 0) * 60 + (m ?? 0)
}

export function minutesToTime(min: number): string {
  const m = ((min % 1440) + 1440) % 1440
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}

/** A shift's activity strip with uncovered time filled by the WORK type. */
export function shiftBlocksFor(shift: Pick<Shift, 'startAt' | 'endAt' | 'activities'>, types: ActivityType[]) {
  const byId = new Map(types.map((t) => [t.id, t]))
  const work = types.find((t) => t.code === 'WORK') ?? types.find((t) => t.category === 'WORK')
  return fillShiftBlocks(
    { start: Date.parse(shift.startAt), end: Date.parse(shift.endAt) },
    shift.activities.map((a) => {
      const t = byId.get(a.activityTypeId)
      return { start: Date.parse(a.startAt), end: Date.parse(a.endAt), activityName: t?.name ?? 'Activity', category: t?.category ?? 'OTHER', color: t?.color ?? '#94A3B8', allowedStates: [] }
    }),
    { activityName: work?.name ?? 'Work', category: 'WORK', color: work?.color ?? '#3366FF', allowedStates: [] },
  ).map((b) => ({ start: b.start, end: b.end, color: b.color ?? null, label: b.activityName }))
}

export interface TimeOffRequest {
  id: string
  agentId: string
  agentName: string
  type: string
  startAt: string
  endAt: string
  allDay: boolean
  reason: string | null
  status: string
  reviewedAt: string | null
  reviewNote: string | null
  createdAt: string
  conflictingShifts: number
}

export interface SwapRequest {
  id: string
  status: string
  note: string | null
  reviewNote: string | null
  createdAt: string
  requesterAgentId: string
  requesterName: string
  targetAgentId: string
  targetName: string
  requesterShiftId: string
  requesterShiftStart: string
  requesterShiftEnd: string
  targetShiftId: string | null
  targetShiftStart: string | null
  targetShiftEnd: string | null
}

export function timeOffSpan(r: { startAt: string; endAt: string; allDay: boolean }, tz: string): string {
  if (r.allDay) {
    const s = new Date(r.startAt).toLocaleDateString('en-US', { timeZone: tz, month: 'short', day: 'numeric' })
    const e = new Date(Date.parse(r.endAt) - 1).toLocaleDateString('en-US', { timeZone: tz, month: 'short', day: 'numeric', year: 'numeric' })
    return s === e.replace(/, \d{4}$/, '') ? e : `${s} – ${e}`
  }
  return `${fmtDateTime(r.startAt, tz)} – ${fmtDateTime(r.endAt, tz)}`
}

/** An agent's real day: sessions, breaks, login/logout events (GET /wfm/me/adherence, /wfm/adherence). */
export type StatusKey = 'available' | 'on_call' | 'ringing' | 'wrap_up' | 'idle' | 'break' | 'logged_out' | 'other'

export interface DayActivity {
  online: boolean
  current: { key: StatusKey; label: string; since: string }
  asOf: string
  statusTimes: { key: StatusKey; label: string; ms: number }[]
  status: string
  currentSessionStart: string | null
  carriedOver: boolean
  firstLogin: string | null
  lastLogout: string | null
  loggedInMs: number
  breakMs: number
  sessions: { start: string; end: string | null; durationMs: number }[]
  breaks: { start: string; end: string | null; label: string }[]
  events: { at: string; type: 'login' | 'logout' | 'break' | 'back'; label: string }[]
}

/** Exact h:mm:ss (or m:ss under an hour). */
export function fmtExact(ms: number): string {
  const t = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(t / 3600)
  const m = Math.floor((t % 3600) / 60)
  const sec = String(t % 60).padStart(2, '0')
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`
}

/** 2h 05m · 12m · 45s */
export function fmtSpan(ms: number): string {
  const sec = Math.max(0, Math.round(ms / 1000))
  if (sec < 60) return `${sec}s`
  const min = Math.round(sec / 60)
  const h = Math.floor(min / 60)
  return h > 0 ? `${h}h ${String(min % 60).padStart(2, '0')}m` : `${min}m`
}

const STATUS_TEXT: Record<StatusKey, string> = {
  available: 'Available', on_call: 'On a call', ringing: 'Ringing', wrap_up: 'Wrap-up',
  idle: 'Idle (not ready)', break: 'Break', logged_out: 'Logged out', other: 'Other',
}

/**
 * The agent's live status bucket from the socket-fed store (mirrors
 * statusKey in routes/wfm/activity.ts). PAUSED is a break when a break status
 * was chosen, otherwise the Ready/Idle toggle (idle).
 */
export function liveStatusOf(status: string | null, userKey: string, userText: string): { key: StatusKey; label: string } | null {
  if (!status) return null
  const s = status.toUpperCase()
  const onBreak = userKey !== 'available' && userKey !== 'login'
  const key: StatusKey =
    s === 'OFFLINE' ? 'logged_out'
      : s === 'IDLE' || s === 'AVAILABLE_ON_DEMAND' ? 'available'
      : ['RESERVING', 'RESERVED', 'INVITING', 'RINGING'].includes(s) ? 'ringing'
      : s === 'ANSWERED' || s === 'IN_CALL' ? 'on_call'
      : s === 'ACW' ? 'wrap_up'
      : s === 'ON_BREAK' ? 'break'
      : s === 'PAUSED' ? (onBreak ? 'break' : 'idle')
      : 'other'
  return { key, label: key === 'break' && onBreak && userText ? userText : STATUS_TEXT[key] }
}
